/**
 * The two product screens the landing page shows a signed-out visitor.
 *
 * These are **props for the real components**, not pictures of them. `FixtureSelection`
 * and `GameProgressBoard` render this data exactly as they render a live game, so the
 * landing page cannot drift away from the product the way a screenshot does the first
 * time somebody restyles a pick button. It also costs no image bytes and works at every
 * breakpoint for free.
 *
 * Everything here is invented, and deliberately so - no real player, game or result is
 * on this page. The clubs are real because a landing page claiming to run the Premier
 * League and then showing "Team A v Team B" is worse than saying nothing.
 *
 * The dates are fixed rather than computed from `now`. A demo that renders a different
 * weekday depending on when it is opened is a demo whose form strips and kick-off times
 * stop agreeing with each other; 6 March 2027 is a Saturday and 7 March a Sunday, which
 * is all these two screens need to be consistent about.
 */

const SATURDAY = '2027-03-06';
const SUNDAY = '2027-03-07';

const TEAMS = {
  arsenal: { id: 1, name: 'Arsenal' },
  chelsea: { id: 2, name: 'Chelsea' },
  liverpool: { id: 3, name: 'Liverpool' },
  city: { id: 4, name: 'Manchester City' },
  spurs: { id: 5, name: 'Tottenham' },
  united: { id: 6, name: 'Manchester United' },
  newcastle: { id: 7, name: 'Newcastle' },
  villa: { id: 8, name: 'Aston Villa' },
};

/** A form strip, written as the five letters it renders as. Newest first, like the API. */
function form(letters, opponents) {
  const outcomes = { W: 'WIN', D: 'DRAW', L: 'LOSS' };
  const overall = letters.split('').map((letter, i) => ({
    fixtureId: `${letters}-${i}`,
    outcome: outcomes[letter],
    opponentName: opponents[i],
    teamScore: letter === 'W' ? 2 : 1,
    opponentScore: letter === 'W' ? 0 : letter === 'D' ? 1 : 2,
  }));
  // The pick screen opens on Overall, and the venue strips are only reachable by tapping
  // - which the landing page freezes. One collection is therefore the whole of it.
  return { overall, home: overall, away: overall };
}

export const DEMO_GAME_WEEK = 12;

export const DEMO_FIXTURES = [
  {
    id: 1,
    locked: false,
    fixture: {
      id: 101,
      homeTeam: TEAMS.arsenal,
      awayTeam: TEAMS.chelsea,
      fixtureDateTime: `${SATURDAY}T15:00:00`,
    },
  },
  {
    id: 2,
    locked: false,
    fixture: {
      id: 102,
      homeTeam: TEAMS.liverpool,
      awayTeam: TEAMS.city,
      fixtureDateTime: `${SATURDAY}T17:30:00`,
    },
  },
  {
    id: 3,
    locked: false,
    fixture: {
      id: 103,
      homeTeam: TEAMS.spurs,
      awayTeam: TEAMS.united,
      fixtureDateTime: `${SUNDAY}T14:00:00`,
    },
  },
];

/**
 * Two teams already spent. Liverpool is in this week's fixtures and greys out in place,
 * which is the rule the whole game turns on; Newcastle is not playing at all, which is
 * why the screen also names the used teams in a line above the list.
 */
export const DEMO_USED_TEAMS = [TEAMS.liverpool, TEAMS.newcastle];

export const DEMO_TEAM_FORM = {
  [TEAMS.arsenal.id]: form('WLWWD', ['Brighton', 'Everton', 'Fulham', 'Wolves', 'Brentford']),
  [TEAMS.chelsea.id]: form('WDDLW', ['Palace', 'Forest', 'Bournemouth', 'Villa', 'Leeds']),
  [TEAMS.liverpool.id]: form('WWDWW', ['Burnley', 'Spurs', 'Everton', 'Wolves', 'Fulham']),
  [TEAMS.city.id]: form('WDWWW', ['Villa', 'Brighton', 'Leeds', 'Palace', 'Forest']),
  [TEAMS.spurs.id]: form('LWDWL', ['Liverpool', 'Bournemouth', 'Brentford', 'Burnley', 'Arsenal']),
  [TEAMS.united.id]: form('DWLWD', ['Wolves', 'Leeds', 'City', 'Everton', 'Brighton']),
};

/**
 * Two rounds of a season, in the shape `GameProgressDto` serialises.
 *
 * Round 12 is live with the picks still hidden, which is the rule worth showing a
 * stranger: the only pick anybody can read before the deadline is their own. Round 11 is
 * settled, so it shows the through/out split that the screen is really for.
 */
export const DEMO_PROGRESS = {
  gameId: 0,
  currentGameWeek: 12,
  playerCount: 9,
  remainingCount: 5,
  tournamentTeamCount: 20,
  rounds: [
    {
      gameWeek: 12,
      tournamentMatchWeek: 28,
      status: 'ACTIVE',
      pickDeadline: `${SATURDAY}T14:00:00`,
      picksRevealed: false,
      current: true,
      survivedCount: 0,
      eliminatedCount: 0,
      pendingCount: 5,
      entries: [
        {
          playerId: 1,
          displayName: 'dave42',
          bot: false,
          you: true,
          outcome: 'PENDING',
          teamId: TEAMS.arsenal.id,
          teamName: 'Arsenal',
          autoPick: false,
          pickConcealed: false,
          teamsRemaining: [
            TEAMS.chelsea, TEAMS.city, TEAMS.spurs, TEAMS.united, TEAMS.villa,
            { id: 9, name: 'Brighton' }, { id: 10, name: 'Everton' },
            { id: 11, name: 'Fulham' }, { id: 12, name: 'Brentford' },
          ],
        },
        {
          playerId: 2,
          displayName: 'groundskeeper42',
          bot: false,
          you: false,
          outcome: 'PENDING',
          teamId: null,
          teamName: null,
          autoPick: false,
          pickConcealed: true,
          teamsRemaining: null,
        },
        {
          playerId: 3,
          displayName: 'sarah_c',
          bot: false,
          you: false,
          outcome: 'PENDING',
          teamId: null,
          teamName: null,
          autoPick: false,
          pickConcealed: true,
          teamsRemaining: null,
        },
        {
          playerId: 4,
          displayName: 'The Gaffer',
          bot: true,
          you: false,
          outcome: 'NO_PICK',
          teamId: null,
          teamName: null,
          autoPick: false,
          pickConcealed: false,
          teamsRemaining: null,
        },
      ],
    },
    {
      gameWeek: 11,
      tournamentMatchWeek: 27,
      status: 'CLOSED',
      pickDeadline: `${SATURDAY}T14:00:00`,
      picksRevealed: true,
      current: false,
      survivedCount: 3,
      eliminatedCount: 2,
      pendingCount: 0,
      entries: [
        {
          playerId: 1,
          displayName: 'dave42',
          bot: false,
          you: true,
          outcome: 'SURVIVED',
          teamId: 9,
          teamName: 'Brighton',
          autoPick: false,
          pickConcealed: false,
          teamsRemaining: null,
        },
        {
          playerId: 2,
          displayName: 'groundskeeper42',
          bot: false,
          you: false,
          outcome: 'SURVIVED',
          teamId: 10,
          teamName: 'Everton',
          autoPick: false,
          pickConcealed: false,
          teamsRemaining: null,
        },
        {
          playerId: 4,
          displayName: 'The Gaffer',
          bot: true,
          you: false,
          outcome: 'SURVIVED',
          teamId: 10,
          teamName: 'Everton',
          autoPick: true,
          pickConcealed: false,
          teamsRemaining: null,
        },
        {
          playerId: 5,
          displayName: 'big_ron',
          bot: false,
          you: false,
          outcome: 'ELIMINATED',
          teamId: 13,
          teamName: 'Wolves',
          autoPick: false,
          pickConcealed: false,
          teamsRemaining: null,
        },
        {
          playerId: 6,
          displayName: 'kev_the_ref',
          bot: false,
          you: false,
          outcome: 'NO_PICK',
          teamId: null,
          teamName: null,
          autoPick: false,
          pickConcealed: false,
          teamsRemaining: null,
        },
      ],
    },
  ],
};
